import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';
import { TradingOfferOperation } from 'types';
import { getTradingOfferOperations } from '../redux/tradingOfferOperationActions';

type UseGetTradingOfferOperationsProps = {
  partnerId?: string;
  tradingOfferId?: string;
  societyId?: string;
  operationStatus?: string;
  tradingOfferType?: string;
  page?: number;
  rowsPerPage?: number;
};

export const useGetTradingOfferOperations = ({
  partnerId,
  tradingOfferId,
  societyId,
  operationStatus,
  tradingOfferType,
  page = 1,
  rowsPerPage = 10,
}: UseGetTradingOfferOperationsProps) => {
  const [tradingOfferOperations, setTradingOfferOperations] = useState<
    TradingOfferOperation[]
  >([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchTradingOfferOperations = useCallback(
    async (controller: AbortController) => {
      if (!societyId) return;

      try {
        setIsLoading(true);

        const { data, total: totalResult } = await store.dispatch(
          getTradingOfferOperations({
            partnerId,
            tradingOfferId,
            societyId,
            params: {
              page,
              items: rowsPerPage,
              tradingOfferOperationStatus: operationStatus,
              tradingOfferType,
            },
            signal: controller.signal,
          })
        );

        if (!controller.signal.aborted) {
          setTradingOfferOperations(data);
          setTotal(totalResult);
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    [
      operationStatus,
      page,
      partnerId,
      rowsPerPage,
      societyId,
      tradingOfferId,
      tradingOfferType,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchTradingOfferOperations(controller);

    return () => controller.abort();
  }, [fetchTradingOfferOperations]);

  return {
    data: { tradingOfferOperations, total },
    isLoading,
    isError,
  };
};
